import {
  coinsQueryOptions,
  popularCoinsQueryOptions,
  TopMoversDirection,
  topMoversQueryOptions,
  TradablePlatform,
} from '@/services/coin/coins'
import { tokenizedStocksQueryOptions } from '@/services/coin/tokenizedStocks'
import { VisitType } from '@/types/coin'

export const COINS_PAGE_SIZE = 100
export const COINS_OG_PAGE_SIZE = 20
export const TOKENIZED_STOCKS_PAGE_SIZE = 50
// HomepageV2 crypto tab keeps the coin list at the same per-page density as
// the Stocks tab (20/page) instead of the default 100-candidate page.
export const CRYPTO_PAGE_SIZE = 20
// HomepageV2 crypto fetches the API in 100-record blocks (not 20) so the API
// page count stays under the backend's deep-paging cap (page*limit ≤ 10000 →
// ~99 real pages at limit=100 vs the ~500-page sentinel wall at limit=20);
// the UI still paginates in CRYPTO_PAGE_SIZE rows by slicing each block.
export const CRYPTO_FETCH_BLOCK_SIZE = 100

export enum CoinTab {
  AllCoins = 'allCoins',
  AllAssets = 'allAssets',
  TopGainers = 'topGainers',
  TopLosers = 'topLosers',
  New = 'new',
  MostPopular = 'mostPopular',
  Favorites = 'favorites',
  TokenizedStocks = 'tokenizedStocks',
}

type TopMoversQueryOptions = ReturnType<typeof topMoversQueryOptions>
type MostPopularQueryOptions = ReturnType<typeof popularCoinsQueryOptions>
type CoinsQueryOptions = ReturnType<typeof coinsQueryOptions>
type TokenizedStocksQueryOptions = ReturnType<typeof tokenizedStocksQueryOptions>

export type CoinTabConfig = {
  labelTranslationKey: string
  value: CoinTab
  coinIdKey: string
} & (
  | {
      getQueryOptions: (page: number) => CoinsQueryOptions
      queryOptions?: never
    }
  | {
      queryOptions:
        | TopMoversQueryOptions
        | MostPopularQueryOptions
        | CoinsQueryOptions
        | TokenizedStocksQueryOptions
      getQueryOptions?: never
    }
)

export const getCoinTabs = (pageSize: number): CoinTabConfig[] => [
  {
    labelTranslationKey: 'allCoins',
    value: CoinTab.AllCoins,
    getQueryOptions: (page: number) =>
      coinsQueryOptions({
        page,
        limit: pageSize,
      }),
    coinIdKey: 'id',
  },
  {
    labelTranslationKey: 'topGainers',
    value: CoinTab.TopGainers,
    queryOptions: topMoversQueryOptions({
      depth: pageSize,
      direction: TopMoversDirection.DESC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    coinIdKey: 'token_id',
  },
  {
    labelTranslationKey: 'topLosers',
    value: CoinTab.TopLosers,
    queryOptions: topMoversQueryOptions({
      depth: pageSize,
      direction: TopMoversDirection.ASC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    coinIdKey: 'token_id',
  },
  {
    labelTranslationKey: 'new',
    value: CoinTab.New,
    queryOptions: coinsQueryOptions({
      page: 1,
      limit: pageSize,
      sort: 'createTime',
      direction: 'DESC',
    }),
    coinIdKey: 'id',
  },
  {
    labelTranslationKey: 'mostPopular',
    value: CoinTab.MostPopular,
    queryOptions: popularCoinsQueryOptions(VisitType.Token),
    coinIdKey: 'key',
  },
  {
    labelTranslationKey: 'favorites',
    value: CoinTab.Favorites,
    queryOptions: popularCoinsQueryOptions(VisitType.Token),
    coinIdKey: 'key',
  },
]

export const coinTabs: CoinTabConfig[] = [
  {
    labelTranslationKey: 'allCoins',
    value: CoinTab.AllCoins,
    getQueryOptions: (page: number) =>
      coinsQueryOptions({
        page,
        limit: COINS_PAGE_SIZE,
      }),
    coinIdKey: 'id',
  },
  {
    labelTranslationKey: 'topGainers',
    value: CoinTab.TopGainers,
    queryOptions: topMoversQueryOptions({
      depth: COINS_PAGE_SIZE,
      direction: TopMoversDirection.DESC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    coinIdKey: 'token_id',
  },
  {
    labelTranslationKey: 'topLosers',
    value: CoinTab.TopLosers,
    queryOptions: topMoversQueryOptions({
      depth: COINS_PAGE_SIZE,
      direction: TopMoversDirection.ASC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    coinIdKey: 'token_id',
  },
  {
    labelTranslationKey: 'new',
    value: CoinTab.New,
    queryOptions: coinsQueryOptions({
      page: 1,
      limit: COINS_PAGE_SIZE,
      sort: 'createTime',
      direction: 'DESC',
    }),
    coinIdKey: 'id',
  },
  {
    labelTranslationKey: 'mostPopular',
    value: CoinTab.MostPopular,
    queryOptions: popularCoinsQueryOptions(VisitType.Token),
    coinIdKey: 'key',
  },
  {
    labelTranslationKey: 'favorites',
    value: CoinTab.Favorites,
    queryOptions: popularCoinsQueryOptions(VisitType.Token),
    coinIdKey: 'key',
  },
]

export const coinTabValues = Object.values(CoinTab)

export const defaultCoinTab = coinTabs[0]

export const getTokenizedStocksTab = (): CoinTabConfig => ({
  labelTranslationKey: 'tokenizedStocks',
  value: CoinTab.TokenizedStocks,
  queryOptions: tokenizedStocksQueryOptions(),
  coinIdKey: 'id',
})

export const getAllAssetsTab = (pageSize: number): CoinTabConfig => ({
  labelTranslationKey: 'allAssets',
  value: CoinTab.AllAssets,
  getQueryOptions: (page: number) => coinsQueryOptions({ page, limit: pageSize }),
  coinIdKey: 'id',
})

export const defaultAllCategoriesOption = {
  tag_code: 'all',
  tag_name: 'All categories',
}

export const defaultAllCoinCategoriesOption = {
  id: 'all',
  name: 'All categories',
  slug: 'all',
  image_url: '',
  market_cap_native: { currency: 'USD', amount: '0' },
  volume_24h_native: { currency: 'USD', amount: '0' },
  percent_change_1h_native: '0',
  percent_change_24h_native: '0',
  highlighted_coins: [],
}
